/**
 * @file KitchenOpsDrawer.tsx
 * @description Kitchen station inventory governance, 86 status toggles, load status, and whiteboard console.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. High-contrast tactile UI optimized for kitchen display terminals and tablet touchscreens.
 * 2. Instant item stock toggling (IN_STOCK, LOW_STOCK, OUT_OF_STOCK / 86).
 * 3. Live portion countdown adjustments (-1 / +1) for auto-86 tracked menu items.
 * 4. Venue kitchen load status governance (NORMAL, BUSY, RUSH, OVERLOAD) controlling guest ordering pacing.
 * 5. Digital kitchen whiteboard for daily prep notes and announcements.
 * 6. Per-dish operational prep notes for culinary station communication.
 */

import React, { useState } from 'react';
import { 
  X, 
  ChefHat, 
  CheckCircle2, 
  AlertTriangle, 
  Power, 
  StickyNote, 
  Search, 
  Clock, 
  Flame, 
  SlidersHorizontal,
  Check,
  RefreshCw,
  UtensilsCrossed,
  Megaphone
} from 'lucide-react';
import { clsx } from 'clsx';
import { useKitchenOps } from '../hooks/useKitchenOps';
import type { KitchenLoadStatus, StockStatus, KitchenOpsItem } from '../types/kitchenOps.types';

interface KitchenOpsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ops: ReturnType<typeof useKitchenOps>;
}

const LOAD_STATUS_CONFIG: Record<KitchenLoadStatus, { label: string; activeClass: string; desc: string }> = {
  NORMAL: {
    label: 'NORMAL',
    activeClass: 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 border-emerald-400',
    desc: 'Standard turnaround. Guest ordering active.',
  },
  BUSY: {
    label: 'BUSY',
    activeClass: 'bg-amber-500 text-black shadow-lg shadow-amber-500/20 border-amber-400 font-black',
    desc: 'Heavy ticket volume. Normal menu operation.',
  },
  RUSH: {
    label: 'RUSH',
    activeClass: 'bg-orange-600 text-white shadow-lg shadow-orange-600/20 border-orange-500',
    desc: 'Peak rush hour. Turnaround delays communicated to guests.',
  },
  OVERLOAD: {
    label: 'OVERLOAD',
    activeClass: 'bg-rose-600 text-white shadow-lg shadow-rose-600/30 border-rose-500 animate-pulse',
    desc: 'Kitchen maxed out. New guest orders automatically paused.',
  },
};

export const KitchenOpsDrawer: React.FC<KitchenOpsDrawerProps> = ({
  isOpen,
  onClose,
  ops,
}) => {
  const {
    filteredItems,
    stations,
    kitchenLoadStatus,
    managerNote,
    chefNote,
    isLoading,
    isSavingStatus,
    selectedStation,
    stockFilter,
    searchQuery,
    outOfStockCount,
    lowStockCount,
    setSelectedStation,
    setStockFilter,
    setSearchQuery,
    setChefNote,
    handleToggleStockStatus,
    handleAdjustRemainingStock,
    handleSaveItemNotes,
    handleChangeLoadStatus,
    handleSaveChefNote,
    refreshOpsData,
  } = ops;

  const [isChefNoteSaved, setIsChefNoteSaved] = useState<boolean>(false);
  const [editingNotesId, setEditingNotesId] = useState<string | null>(null);
  const [localNoteText, setLocalNoteText] = useState<string>('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-4xl h-full bg-zinc-950 border-l border-zinc-800 flex flex-col shadow-2xl overflow-hidden pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {/* Top Drawer Header */}
        <div className="p-4 sm:p-5 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/60 shrink-0">
          <div className="flex items-center gap-3 min-w-0 pr-3">
            <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-400 border border-amber-500/20 shrink-0">
              <SlidersHorizontal className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-lg font-black text-white uppercase tracking-tight truncate">
                  KITCHEN OPS & 86 CONTROLS
                </h2>
                {outOfStockCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/30 text-rose-400 text-[10px] font-black uppercase">
                    {outOfStockCount} 86'd
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-400 truncate">
                Station inventory health, daily prep notes, and kitchen load pacing
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={refreshOpsData}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
              title="Refresh inventory"
              aria-label="Refresh inventory"
            >
              <RefreshCw className={clsx("w-4 h-4", isLoading && "animate-spin text-amber-400")} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Main Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          {/* Section 1: Kitchen Load Status Governance */}
          <div className="p-4 sm:p-5 rounded-2xl bg-zinc-900/40 border border-zinc-800/80 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-black uppercase text-zinc-300 tracking-wider">
                  KITCHEN LOAD STATUS
                </span>
              </div>
              <span className="text-[11px] text-zinc-400 font-medium hidden sm:inline">
                {LOAD_STATUS_CONFIG[kitchenLoadStatus]?.desc}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(['NORMAL', 'BUSY', 'RUSH', 'OVERLOAD'] as const).map((status) => {
                const isSelected = kitchenLoadStatus === status;
                const config = LOAD_STATUS_CONFIG[status];
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleChangeLoadStatus(status)}
                    disabled={isSavingStatus}
                    className={clsx(
                      "min-h-[44px] px-3 py-2.5 rounded-xl border text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-1.5",
                      isSelected
                        ? config.activeClass
                        : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:text-white hover:bg-zinc-800/60"
                    )}
                  >
                    {status === 'OVERLOAD' && <Power className="w-3.5 h-3.5" />}
                    <span>{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section 2: Dual Kitchen Whiteboards (Manager's Broadcast + Chef's Line Prep) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Whiteboard 1: Manager's Broadcast Note */}
            <div className="p-4 sm:p-5 rounded-2xl bg-indigo-500/[0.05] border border-indigo-500/20 flex flex-col space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-indigo-400" />
                  <span className="text-xs font-black uppercase text-indigo-400 tracking-wider">
                    MANAGER'S BROADCAST
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-[10px] font-bold uppercase tracking-wider">
                  Management Note
                </span>
              </div>

              <div className="flex-1 min-h-[72px] bg-zinc-950/80 border border-zinc-800 rounded-xl p-3 flex flex-col justify-center">
                {managerNote?.trim() ? (
                  <p className="text-xs font-semibold text-zinc-100 whitespace-pre-wrap leading-relaxed select-text">
                    {managerNote}
                  </p>
                ) : (
                  <p className="text-xs text-zinc-600 italic">
                    No active announcements or instructions broadcast from management for this shift.
                  </p>
                )}
              </div>
            </div>

            {/* Whiteboard 2: Chef's Line Prep Whiteboard */}
            <div className="p-4 sm:p-5 rounded-2xl bg-amber-500/[0.05] border border-amber-500/20 flex flex-col space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-black uppercase text-amber-400 tracking-wider">
                    CHEF'S LINE WHITEBOARD
                  </span>
                </div>
                {isChefNoteSaved && (
                  <span className="flex items-center gap-1 text-[11px] font-bold text-emerald-400">
                    <Check className="w-3.5 h-3.5" /> Saved
                  </span>
                )}
              </div>

              <textarea
                rows={2}
                placeholder="Post soup of the day, 86 warnings, or shift prep notes for the kitchen crew..."
                value={chefNote}
                onChange={(e) => {
                  setChefNote(e.target.value);
                  setIsChefNoteSaved(false);
                }}
                onBlur={(e) => {
                  handleSaveChefNote(e.target.value);
                  setIsChefNoteSaved(true);
                  setTimeout(() => setIsChefNoteSaved(false), 2500);
                }}
                className="w-full flex-1 min-h-[72px] bg-zinc-950/80 border border-zinc-800 rounded-xl p-3 text-xs font-semibold text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all resize-none"
              />
            </div>
          </div>

          {/* Section 3: Station Tabs, Search, and 86 Filter Toolbar */}
          <div className="space-y-3">
            {/* Station Filter Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
              <button
                type="button"
                onClick={() => setSelectedStation(null)}
                className={clsx(
                  "min-h-[38px] px-3.5 py-1.5 rounded-xl border text-[11px] font-black uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer",
                  !selectedStation
                    ? "bg-white text-black border-white shadow-sm"
                    : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white hover:bg-zinc-800"
                )}
              >
                All Stations
              </button>
              {stations.map((st) => (
                <button
                  key={st}
                  type="button"
                  onClick={() => setSelectedStation(st)}
                  className={clsx(
                    "min-h-[38px] px-3.5 py-1.5 rounded-xl border text-[11px] font-black uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer",
                    selectedStation === st
                      ? "bg-amber-500 text-black border-amber-400 shadow-sm"
                      : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-white hover:bg-zinc-800"
                  )}
                >
                  {st}
                </button>
              ))}
            </div>

            {/* Search + Stock Filter Pills */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 bg-zinc-900/50 p-2.5 sm:p-3 rounded-2xl border border-zinc-800/70">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" size={15} />
                <input
                  type="text"
                  placeholder="Search dish name, station, notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl pl-9 pr-8 py-2 text-xs font-semibold text-white placeholder:text-zinc-600 outline-none focus:border-amber-500/50 transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white cursor-pointer"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {(['ALL', 'LOW', 'OUT'] as const).map((filterVal) => {
                  const label = filterVal === 'ALL' ? 'All' : filterVal === 'LOW' ? `Low (${lowStockCount})` : `86'd (${outOfStockCount})`;
                  return (
                    <button
                      key={filterVal}
                      type="button"
                      onClick={() => setStockFilter(filterVal)}
                      className={clsx(
                        "min-h-[36px] px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer",
                        stockFilter === filterVal
                          ? filterVal === 'OUT'
                            ? "bg-rose-600 text-white border-rose-500"
                            : filterVal === 'LOW'
                            ? "bg-amber-500 text-black border-amber-400"
                            : "bg-white text-black border-white"
                          : "bg-zinc-950 text-zinc-400 border-zinc-800 hover:text-white"
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Section 4: Items Grid */}
          {isLoading && filteredItems.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 border-3 border-amber-500/20 border-t-amber-500 rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs text-zinc-500 font-bold uppercase tracking-wider">Loading station items...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 px-4 bg-zinc-900/30 rounded-3xl border border-zinc-800/60">
              <UtensilsCrossed className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <h3 className="text-sm font-black text-white uppercase tracking-wider mb-1">
                No items match filters
              </h3>
              <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-4">
                {searchQuery || selectedStation || stockFilter !== 'ALL'
                  ? 'Try clearing your search query or station filters to see all dishes.'
                  : 'No items found in this venue catalog.'}
              </p>
              {(searchQuery || selectedStation || stockFilter !== 'ALL') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedStation(null);
                    setStockFilter('ALL');
                  }}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-200 hover:text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {filteredItems.map((item) => {
                const autoStock = item.availability_settings?.auto_stock_enabled;
                const remaining = item.availability_settings?.remaining_stock ?? 0;
                const initial = item.availability_settings?.initial_stock ?? 50;
                const lowThreshold = item.availability_settings?.low_stock_threshold ?? 5;
                const isOutOfStock = item.stock_status === 'OUT_OF_STOCK';
                const isLowStock = item.stock_status === 'LOW_STOCK';

                return (
                  <div
                    key={item.id}
                    className={clsx(
                      "p-4 rounded-2xl border transition-all duration-200 flex flex-col justify-between space-y-3",
                      isOutOfStock
                        ? "bg-rose-950/20 border-rose-500/40"
                        : isLowStock
                        ? "bg-amber-950/15 border-amber-500/30"
                        : "bg-zinc-900/70 border-zinc-800 hover:border-zinc-700"
                    )}
                  >
                    {/* Top Row: Station badge & prep time */}
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-lg bg-zinc-800 text-zinc-300 border border-zinc-700/50">
                          {item.kitchen_station || 'MAIN KITCHEN'}
                        </span>
                        <span className="text-[10px] text-zinc-500 font-mono font-bold flex items-center gap-1">
                          <Clock className="w-3 h-3 text-zinc-500" />
                          {item.prep_time_minutes || 15}m
                        </span>
                      </div>

                      {/* Dish Name */}
                      <h4 className="text-sm font-black text-white uppercase tracking-tight line-clamp-1">
                        {item.name}
                      </h4>
                    </div>

                    {/* Portions Countdown (Auto-86) */}
                    {autoStock && (
                      <div className="p-2.5 rounded-xl bg-zinc-950/70 border border-zinc-800 flex items-center justify-between">
                        <div className="min-w-0 pr-2">
                          <span className="text-[9px] font-black uppercase text-zinc-400 block tracking-wider">
                            Remaining
                          </span>
                          <span
                            className={clsx(
                              "text-xs font-mono font-black",
                              remaining <= 0
                                ? "text-rose-400"
                                : remaining <= lowThreshold
                                ? "text-amber-400"
                                : "text-emerald-400"
                            )}
                          >
                            {remaining} / {initial}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleAdjustRemainingStock(item.id, -1)}
                            className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white font-mono font-black text-xs flex items-center justify-center transition-all cursor-pointer border border-zinc-700"
                            title="Decrement 1 portion"
                          >
                            -1
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAdjustRemainingStock(item.id, 1)}
                            className="w-8 h-8 rounded-lg bg-zinc-800 hover:bg-zinc-700 active:scale-95 text-white font-mono font-black text-xs flex items-center justify-center transition-all cursor-pointer border border-zinc-700"
                            title="Increment 1 portion"
                          >
                            +1
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Stock Status Selector Buttons */}
                    <div className="grid grid-cols-3 gap-1.5 pt-1">
                      {[
                        { status: 'IN_STOCK' as StockStatus, label: 'STOCK', color: 'bg-emerald-500 text-white border-emerald-400' },
                        { status: 'LOW_STOCK' as StockStatus, label: 'LOW', color: 'bg-amber-500 text-black border-amber-400 font-black' },
                        { status: 'OUT_OF_STOCK' as StockStatus, label: '86 OUT', color: 'bg-rose-600 text-white border-rose-500 font-black' },
                      ].map((btn) => {
                        const isBtnActive = item.stock_status === btn.status;
                        return (
                          <button
                            key={btn.status}
                            type="button"
                            onClick={() => handleToggleStockStatus(item.id, btn.status)}
                            className={clsx(
                              "min-h-[38px] px-2 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center",
                              isBtnActive
                                ? btn.color
                                : "bg-zinc-950 text-zinc-500 border-zinc-800 hover:text-zinc-200 hover:bg-zinc-900"
                            )}
                          >
                            {btn.label}
                          </button>
                        );
                      })}
                    </div>

                    {/* Quick Item Prep Notes */}
                    <div className="pt-1 border-t border-zinc-800/60">
                      {editingNotesId === item.id ? (
                        <input
                          type="text"
                          placeholder="Special prep note..."
                          value={localNoteText}
                          onChange={(e) => setLocalNoteText(e.target.value)}
                          onBlur={() => {
                            handleSaveItemNotes(item.id, localNoteText);
                            setEditingNotesId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveItemNotes(item.id, localNoteText);
                              setEditingNotesId(null);
                            }
                          }}
                          autoFocus
                          className="w-full bg-zinc-950 border border-amber-500/50 rounded-lg px-2.5 py-1 text-[11px] text-white outline-none"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingNotesId(item.id);
                            setLocalNoteText(item.internal_notes || '');
                          }}
                          className="w-full text-left text-[10px] font-medium text-zinc-400 hover:text-amber-400 truncate flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <span className="text-zinc-600 font-bold shrink-0">Note:</span>
                          <span className="truncate italic">
                            {item.internal_notes || 'Tap to add prep instruction...'}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
